/**
 * Who owns the create card while the sender is composing (kitecorp/transferify-ui#1549).
 *
 * Plain TypeScript — this module imports no React — because three surfaces compete for one box and
 * "which one is on top, and is the panel still usable underneath it" is a rule, not markup. It was
 * spread across two conditions in {@link ./CreateFlow} before this ticket, which is how the consent
 * gate came to REMOVE the product from the page rather than stand in front of it.
 *
 * <b>The gate covers, the verify step replaces — and that difference is the whole of ui#1549.</b>
 *
 * - The CONSENT gate (ui#1170) is answered by a visitor who has not yet seen what they are agreeing
 *   about. Since ui#1575 it really does sit OVER the panel — a bar floating at the create card's lower
 *   edge, taking no room in the card's flow — so the upload box stays visible to the visitor and to a
 *   crawler and stays where it is, while the gate still blocks every interaction, so nobody uploads
 *   without accepting. Before ui#1549, `terms.open` also hid the panel
 *   and a paid click landed on a legal notice where the product was supposed to be (measured: the drop
 *   control reported a 0x0 box on arrival); between ui#1549 and ui#1575 the gate was in flow instead,
 *   which kept the control on the page but pushed it 212px down whenever the gate was up.
 * - The email-code VERIFY step (ui#132) is answered by a sender who has already composed a transfer
 *   and pressed Send. There is nothing to look at behind it and the card would be twice as tall for
 *   no reason, so it keeps replacing the panel — hidden, never unmounted, so "use a different email"
 *   returns to the same draft.
 *
 * Blocking is expressed as NOT INTERACTIVE rather than as unmounting, deliberately: a draft composed
 * before a destination sheet opened, or files dropped on the page while the gate was up, must still be
 * there afterwards. That is the same `covered` mechanism ui#1061 introduced — see
 * {@link ./components/TransferPanel}, where it becomes `inert` on the control block.
 */

/** What is standing over the create card right now. */
export interface DropAreaState {
	/** A destination CardSheet covers the drop area without unmounting it (ui#1061). */
	covered: boolean;
	/** This browser identity has not accepted the current policy set (ui#1170). */
	consentOpen: boolean;
	/** The email-code confirmation has taken over the card (ui#132). */
	verifying: boolean;
}

/** What the create card renders, and what the sender may do with it. */
export interface DropAreaLayout {
	/** The panel stays mounted but leaves the layout entirely — the verify step's idiom. */
	panelHidden: boolean;
	/** The panel accepts pointer, keyboard and assistive-technology interaction. */
	panelInteractive: boolean;
	/**
	 * The consent gate is rendered over the panel — out of the card's flow since ui#1575, so raising it
	 * moves nothing underneath it. WHERE over the panel is {@link ./CreateFlow.styles.ConsentDock}'s;
	 * this flag only says whether it is there at all.
	 *
	 * Nothing else rides it. A capability pitch and a drop-area size line were tied to this lifecycle
	 * at ui#1549 and removed outright on 2026-08-27 (owner), so the gate drives the gate and the panel
	 * underneath it looks the same in both states.
	 *
	 * It varies by USER STATE and by nothing else. There is no user-agent check anywhere on this path
	 * and there must never be one: a crawler arrives with no stored acceptance, so it is served exactly
	 * what a first-time human is served, which is the difference between personalization and cloaking.
	 */
	consentVisible: boolean;
}

export function resolveDropArea({covered, consentOpen, verifying}: DropAreaState): DropAreaLayout {
	return {
		panelHidden: verifying,
		// Every reason the panel is obscured lands on ONE flag, so a surface can never be raised over a
		// panel that is still clickable behind it.
		panelInteractive: !covered && !consentOpen && !verifying,
		// Never both: the verify step is a full replacement, so a gate drawn over it would float above
		// an empty card. Unreachable in practice — a send cannot start while the gate is up — and stated
		// anyway, because "unreachable" is a property of today's call sites, not of this rule.
		consentVisible: consentOpen && !verifying,
	};
}
